import type { CodShortfallReason, CodTenderOptions, DriverShipment } from "@khazzin/api-client";
import { formatMoney, type Money, parseMoney } from "@khazzin/i18n/money";
import { StatusChip } from "@khazzin/ui/patterns/StatusChip";
import { Button, Field, Input } from "@khazzin/ui/primitives";
import { useState } from "react";
import { useI18n } from "../../i18n";
import { useCommand, useOffline } from "../../offline/Offline";
import {
	booked,
	COLLECT,
	codStep,
	collectPayload,
	convertMinor,
	gate,
	paidMinor,
	rateOf,
	SHORTFALL_REASONS,
	type TenderLine,
} from "./cod";

/**
 * Cash at the door (DA-10-20, EP10-S19): on a delivered COD stop, the
 * **Expected** amount — the server's — what the customer handed over, and a
 * required reason when it is short; **Confirm cash** queues `collect_cod`, so
 * it works with no signal and the Cash tab counts it at once.
 *
 * A payment may be split over currencies (COD phase 2a): the order's currency
 * first, and any currency with today's rate added beside it, each line shown
 * at its value in the order's currency. More than due is refused, as the
 * server refuses it: the driver hands back the change.
 */
export function CodConfirm({ stop }: { stop: DriverShipment }) {
	const { t, locale } = useI18n();
	const { wallet, outbox, tenderOptions } = useOffline();
	const collect = useCommand(COLLECT);
	const step = codStep(stop, wallet, outbox, tenderOptions);
	if (step.kind === "none") {
		return null;
	}
	const money = (amountMinor: number) =>
		formatMoney({ amountMinor, currency: stop.cod?.currency ?? "USD" }, locale);

	return (
		<section aria-label={t("driver.cod.title")} className="flex flex-col gap-3 card p-4">
			<h2 className="text-[15px] font-bold text-fg">{t("driver.cod.title")}</h2>
			{step.kind === "awaiting" && (
				<p className="text-[14px] text-fg-muted">{t("driver.cod.awaiting")}</p>
			)}
			{(step.kind === "queued" || step.kind === "confirmed") && (
				<div className="flex flex-wrap gap-1.5">
					<StatusChip tone="success" size="sm">
						{t("driver.cod.confirmed", { amount: money(step.collectedMinor) })}
					</StatusChip>
					{step.kind === "queued" && (
						<StatusChip tone="neutral" size="sm">
							{t("driver.outcome.queued")}
						</StatusChip>
					)}
				</div>
			)}
			{step.kind === "confirm" && (
				<CodForm
					due={step.due}
					options={tenderOptions}
					onConfirm={(lines, bookedMinor, reason) =>
						collect(collectPayload(step.due.amountMinor, lines, bookedMinor, reason, new Date()), {
							shipmentId: stop.shipmentId,
						})
					}
				/>
			)}
		</section>
	);
}

export interface CodFormProps {
	due: Money;
	/** Today's currencies and rates; null or the base alone offers the order's currency only. */
	options?: CodTenderOptions | null;
	onConfirm: (
		lines: TenderLine[],
		bookedMinor: number,
		reason: CodShortfallReason | undefined,
	) => Promise<void>;
}

/** The cash taken, a line a currency, and, when short, why. Confirm waits for both. */
export function CodForm({ due, options = null, onConfirm }: CodFormProps) {
	const { t, locale } = useI18n();
	const [texts, setTexts] = useState<{ currency: string; text: string }[]>([
		{ currency: due.currency, text: "" },
	]);
	const [reason, setReason] = useState<CodShortfallReason>();
	const [busy, setBusy] = useState(false);

	// A line typed in its own currency; an empty one counts as nothing, one
	// that does not read as an amount stops the confirmation.
	const parsed = texts.map((l) => ({
		currency: l.currency,
		amountMinor: l.text.trim() === "" ? 0 : parseMoney(l.text, l.currency, locale),
	}));
	const unreadable = parsed.some((l, i) => l.amountMinor === null && texts[i]?.text.trim() !== "");
	const lines = parsed.map((l) => ({ currency: l.currency, amountMinor: l.amountMinor ?? 0 }));
	const typed = texts.some((l) => l.text.trim() !== "");
	const foreign = lines.some((l) => l.currency !== due.currency && l.amountMinor > 0);
	const paid = paidMinor(lines, due.currency, options);
	const tendered =
		!typed || unreadable || paid === null ? null : booked(due.amountMinor, paid, foreign);
	const verdict = gate(due.amountMinor, tendered, reason);
	const short = tendered !== null && tendered >= 0 && tendered < due.amountMinor;
	const addable = (options?.options ?? [])
		.map((o) => o.currency)
		.filter((code) => !texts.some((l) => l.currency === code))
		.filter(() => rateOf(options, due.currency) !== undefined);

	async function confirm() {
		if (tendered === null) {
			return;
		}
		setBusy(true);
		try {
			await onConfirm(lines, tendered, short ? reason : undefined);
		} finally {
			setBusy(false);
		}
	}

	return (
		<div className="flex flex-col gap-3">
			<p className="flex items-baseline justify-between gap-2 text-[14px] text-fg-muted">
				{t("driver.cod.expected")}
				<span className="text-[20px] font-bold text-fg">{formatMoney(due, locale)}</span>
			</p>
			{texts.map((line, i) => {
				const from = rateOf(options, line.currency);
				const to = rateOf(options, due.currency);
				const amount = lines[i]?.amountMinor ?? 0;
				const value =
					line.currency === due.currency || from === undefined || to === undefined || amount <= 0
						? null
						: convertMinor(amount, line.currency, from, due.currency, to);
				return (
					<div key={line.currency} className="flex flex-col gap-1">
						<Field label={t("driver.cod.tendered", { currency: line.currency })} required={i === 0}>
							{(props) => (
								<Input
									{...props}
									inputMode="decimal"
									autoComplete="off"
									className="h-12 text-end text-[18px]"
									value={line.text}
									onChange={(e) =>
										setTexts(texts.map((l, j) => (j === i ? { ...l, text: e.target.value } : l)))
									}
								/>
							)}
						</Field>
						{value !== null && (
							<p className="text-end text-[13px] text-fg-muted">
								{t("driver.cod.converted", {
									amount: formatMoney({ ...due, amountMinor: value }, locale),
								})}
							</p>
						)}
						{i > 0 && (
							<Button
								variant="ghost"
								size="sm"
								onClick={() => setTexts(texts.filter((_, j) => j !== i))}
							>
								{t("driver.cod.removeCurrency", { currency: line.currency })}
							</Button>
						)}
					</div>
				);
			})}
			{addable.length > 0 && (
				<div className="flex flex-wrap gap-2">
					{addable.map((code) => (
						<Button
							key={code}
							variant="secondary"
							size="sm"
							onClick={() => setTexts([...texts, { currency: code, text: "" }])}
						>
							{t("driver.cod.addCurrency", { currency: code })}
						</Button>
					))}
				</div>
			)}
			{foreign && paid !== null && (
				<p className="text-[14px] text-fg">
					{t("driver.cod.paid", { amount: formatMoney({ ...due, amountMinor: paid }, locale) })}
				</p>
			)}
			{short && (
				<p className="text-[14px] font-bold text-warning-fg">
					{t("driver.cod.short", {
						amount: formatMoney({ ...due, amountMinor: due.amountMinor - (tendered ?? 0) }, locale),
					})}
				</p>
			)}
			{verdict === "over" && (
				<p role="alert" className="text-[14px] text-danger-fg">
					{t("driver.cod.over")}
				</p>
			)}
			{unreadable && (
				<p role="alert" className="text-[14px] text-danger-fg">
					{t("driver.cod.invalid")}
				</p>
			)}
			{short && (
				<fieldset className="flex flex-col gap-1">
					<legend className="mb-2 text-[14px] font-bold text-fg">
						{t("driver.cod.reasonTitle")}
					</legend>
					{SHORTFALL_REASONS.map((r) => (
						<label
							key={r}
							className="flex min-h-12 items-center gap-3 rounded-lg border border-border px-3 text-[15px] text-fg has-checked:border-brand has-checked:bg-brand-subtle"
						>
							<input
								type="radio"
								name="shortfall-reason"
								className="size-5 accent-brand"
								checked={reason === r}
								onChange={() => setReason(r)}
							/>
							{t(`driver.cod.reason.${r}`)}
						</label>
					))}
				</fieldset>
			)}
			<Button
				variant="primary"
				size="lg"
				disabled={verdict !== "ok"}
				loading={busy}
				onClick={() => void confirm()}
			>
				{t("driver.cod.confirm")}
			</Button>
		</div>
	);
}
